import { Id } from "react-toastify";

import {
  AgencyBrandCountType,
  AgenticLogicValidationScenarioInput,
  AiNodeContext,
  BlockTemplateType,
  CheckoutBlockType,
  DeviceType,
  DiscountSettingFieldType,
  EmbeddedImage,
  ExperienceCategory,
  ExperienceDto,
  ExperienceListDto,
  ExpressionNodeType,
  FlaggedName,
  FlatAgencyOrgRow,
  GenericExperienceFe,
  OfferPageType,
  QaCheckResultDto,
  RedirectDto,
  RenderLocation,
  ScheduleDto,
  ShareSettings,
  ShopifyJobType,
  StripeProductVersion,
  StripeSubscriptionSummaryDto,
  VariationFe,
  WebsocketData,
  WidgetCategory,
  WidgetType,
} from "@intelligems/ig-types";
import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Action } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/types";
import type { RootState } from "@src/store/rootState";
import { Experience } from "@src/store/slices/experiences/types";
import { FoundExperimentDetails } from "@src/types/experience";
import type { AgenticLogicSurfaceDescriptor } from "@src/features/ai/pages/agentic-logic/surface/surface";

type WebsocketToast = Record<
  string, // experiment id
  Record<
    ShopifyJobType,
    {
      toast?: Id;
      progress: number;
    }
  >
>;

export interface DialogProps {
  firstExperienceLaunched: void;
  forceReload: void;
  setDomain: void;
  duplicateShippingTest: void;
  imageLibrary: {
    id: string;
    onUpdateImage?: (imageUrl: string) => void;
  };
  multiImageSelect: {
    images: EmbeddedImage[];
    onSelect: (image: EmbeddedImage) => void;
  };
  checkoutBlockShopifySetup: void;
  postPurchaseShopifySetup: void;
  postPurchaseTypePicker: { variationId: string };
  billingStatus: Experience;
  stayAi: void;
  googleAnalyticsIntegration: void;
  intelligemsApiIntegration: void;
  upsAccount: void;
  metaAdsAccounts: void;
  freeTrial: { isPersonalization: boolean };
  largeDevicePreview: {
    device: DeviceType;
    url: string | undefined;
  };
  completePricingIntegration: Experience;
  requestIntegration: void;
  upgradeToProfitOptimization: {
    couponId: string | undefined;
    priceIds: string[];
    action: "upgrade" | "create";
    stripeSubscriptionLinkId: string;
  };
  offersMarketingDialog: void;
  warningBase: void;
  manageSubscriptions: {
    action: Action;
    stripeSubscriptionLinkId?: string | null;
    showPauseProps?: { planName: string; status: string };
  };
  addModifications: void;
  onsiteEditsSave: void;
  experienceBuilder: { modificationName: string };
  onsiteEditorDeprecation: {
    onAcknowledge: () => void;
    onContactUs: () => void;
  };
  onsiteEditRefresh: void;
  redirectToPlugin: {
    id: string;
    experience: Experience | ExperienceDto | undefined;
    path?: string;
  };
  rollouts: {
    variationId?: string;
    filterTargeting?: void;
    tableTargeting?: ExpressionNodeType[];
    testTargeting?: void;
    experimentGoals?: void;
    priceIds?: string[];
    action?: "upgrade" | "downgrade";
    stripeSubscriptionLinkId?: string;
  } | void;
  waitForShopify: void;
  modifyStripeSubscription: void;
  createExperiment: {
    hideBackButton?: boolean;
    showContentSection?: boolean;
    filterKeys: string[];
  } | void;
  setDuplicateProductStatus: void;
  shippingRates: void;
  activeSchedule: {
    schedules: ScheduleDto[];
    experience: ExperienceListDto | undefined;
  };
  priceUpdater: { action: "pause" | "end" | "start" | "none" | "reset" };
  priceUpdaterStart: void;
  earlyTestReport: {
    experience: {
      id: string;
      name: string;
      type: string;
      category: ExperienceCategory;
    };
    action: "end" | "pause";
    daysLive: number;
  };
  marketPriceUpdater: { action: "pause" | "end" | "start" | "none" | "reset" };
  marketPriceUpdaterStart: void;
  marketUpdateConfirm: {
    action: "pause" | "end" | "start" | "none" | "reset";
    testGroupId?: string;
  };
  switchAudienceType: {
    newTab: number;
  };
  switchExpressionType: {
    newTab: number;
    queryParamIndex: number;
  };
  share: { isImpact: boolean; id: string };
  checkoutScript: void;
  checkoutBlocks: {
    isEdit?: boolean;
    isCreate?: boolean;
    bypassEnableScreen?: boolean;
    checkoutBlockType?: CheckoutBlockType;
    checkoutBlockTemplate?: string;
    // Only the scratch-card create flow sets this; on close/complete the dialog
    // routes back to the Checkout list. Subsequent "Add another block" dialogs
    // omit it so they stay in the current experience.
    returnToCheckoutListOnClose?: boolean;
  };
  postPurchase: {
    isEdit?: boolean;
    isCreate?: boolean;
    postPurchaseType?: string;
    bypassEnableScreen?: boolean;
    variationId: string;
  };
  priceTestCurrency: void;
  campaignTestCreateNew: void;
  scriptTag: void;
  bulkSyncExperienceProducts: void;
  downloadPriceCsv: void;
  shareTestResults: { experienceId: string };
  devicePreviewPath: void;
  updatedDetected: void;
  missingShopifyScope: {
    initial?: boolean;
  } | void;
  confirmExperienceDeletion: {
    experience: Experience;
    category: ExperienceCategory;
    redirect: boolean;
    usedInTest?: FoundExperimentDetails;
  };
  confirmReset: {
    action: "reset";
    experienceId: string;
    experience: Experience;
  };
  confirmActiveExperimentSave: void;
  chooseContentTestType: void;
  scoped: {
    title: string;
    message: string;
    yesButtonText?: string;
    yesButtonAction?: () => void;
    noButtonText?: string;
    noButtonAction?: () => void;
    showButtons?: boolean;
    showProductLimit?: boolean;
  };
  confirm: {
    title: string;
    message: string;
    confirmText?: string;
    confirmAction?: () => void;
    cancelText?: string;
    cancelAction?: () => void;
    confirmColor?: "error" | "primary" | "secondary";
    tertiaryText?: string;
    tertiaryAction?: () => void;
    closeAction?: () => void;
    showCloseIcon?: boolean;
  };
  priceTemplateDownload: void;
  autoFillProductPrices: void;
  scheduleTest: {
    experience: GenericExperienceFe;
  };
  schedulePersonalization: {
    experience: GenericExperienceFe;
    schedules: ScheduleDto[];
  };
  onboarding: void;
  connectToShopify: void;
  productTableWarning: void;
  deleteDiscountTier: void;
  dashboardFilters: void;
  analyticsFilters: void;
  editTestGroups: void;
  testSave: void;
  startTest: { title: string; text: string };
  endNonPriceTest: { defaultToRollout: boolean } | void;
  pauseNonPriceTest: { defaultToRollout: boolean } | void;
  previewTestGroupCode: {
    id?: string;
    path?: string;
    themeId?: string;
  };
  previewExperiment: {
    id: string;
    path?: string;
    themeId?: string;
  };
  dataExport: void;
  groupWinners: void;
  analyticsViewType: void;
  startShippingError: void;
  thirdPartyCarrierWarning: void;
  adminOverrideStartExp: void;
  confirmDuplicateProductTestDuplicate: {
    onConfirm?: () => void;
  };
  discountApplicationTypeSwitch: void;
  showUserInfo: void;
  experienceProductIssues: { showStartTestRow: boolean };
  experienceProductIssuesRow: { rowId: string };
  duplicateExperienceToGroup: {
    renderLocation: RenderLocation;
  };
  redirectAssetQuickView: {
    variation: VariationFe;
    redirectState: RedirectDto;
    screenshotPreviewPath: string;
    previewUrl: string;
  };
  templateSnippetGate: void;
  testNameReviewDialog: { experienceId: string; experienceName: string };
  experienceSaveConfirmation: void;
  unsavedTestChanges: {
    title?: string;
    subtitle?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel: () => void;
  };
  discardConfigureAnalyticsChanges: void;
  insufficientPlan: void;
  upgradeToActivate: void;
  createPersonalization?: { initial?: boolean; dontNavigate?: boolean };
  confirmIgChecklist: { isAdminOverride?: boolean; isSchedule?: boolean };
  offerLimitReached: void;
  ineligibleProductsSelected: void;
  archivePersonalization: {
    experienceId: string;
    repopulate?: boolean;
    shouldNavigate?: boolean;
  };
  unarchivePersonalization: { experienceId: string; shouldNavigate?: boolean };
  stopThemePersonalization: { onConfirm: () => void };
  qaLaunchReview: {
    checkResults: QaCheckResultDto[];
    onContinue: () => void;
    reviewState: "issues_found" | "stale";
  };
  saveWidgetConfirm: {
    widgetId: string;
    variationId: string;
    widgetType: WidgetType;
  };
  removeArchivedKeyMetric: {
    index: number;
  };
  copyPersonalization: void;
  relatedExperienceActive: {
    personalization: Experience;
    isUsedInTestName: string | undefined;
  };
  relatedPersonalizationActive: {
    experience: ExperienceListDto;
    isUsed: FoundExperimentDetails[];
  };
  archiveCustomEvent: void;
  unarchiveCustomEvent: void;
  offers: {
    isEdit?: boolean;
    isCreate?: boolean;
    targetPage?: OfferPageType;
  };
  addOfferModifications: void;
  deleteComponent: {
    componentType: WidgetCategory;
    variationId: string;
    widgetType?: WidgetType;
  };
  removeComponent: {
    variationId: string;
    minimumPurchaseChange?: { checked: boolean };
    discountTypeChange?: { key: DiscountSettingFieldType; value: string };
    productId?: string;
  };
  quantityButtonsFullPageEditor: {
    widgetId?: string;
    variationId: string;
    widgetType: "quantityButtons";
  };
  messageBoxFullPageEditor: {
    widgetId?: string;
    variationId: string;
    widgetType: "messageBoxPopup" | "messageBoxSlideOut";
  };
  offerProgressBarFullPageEditor: {
    widgetId?: string;
    variationId: string;
    widgetType: "discountProgressBar";
  };
  shippingProgressBarFullPageEditor: {
    widgetId?: string;
    variationId: string;
    widgetType: "shippingProgressBar";
  };
  subscriptionDiscount: void;
  cmdk: void;
  confirmRemoveBrandFromAgencyDialog: {
    brandDetails: FlatAgencyOrgRow;
    allPossibleBrands: Record<
      string,
      {
        features: string[];
        isMissing: boolean;
        level?: number;
        version: StripeProductVersion;
        stripeProductId: string;
      }
    >;
    brandInfo?: Record<string, AgencyBrandCountType>;
  };
  updateBrandPlanUnderAgency: {
    brandDetails: FlatAgencyOrgRow;
    allPossibleBrands: Record<
      string,
      {
        features: string[];
        isMissing: boolean;
        level?: number;
        version: StripeProductVersion;
        stripeProductId: string;
      }
    >;
    brandInfo?: Record<string, AgencyBrandCountType>;
    productSubscriptionMap?: Record<
      string,
      {
        subscriptionId: string;
        productId: string;
        version: StripeProductVersion;
      }
    >;
  };
  newPlanOverview: {
    planName: {
      name: string;
      stripeProductId: string;
      version: StripeProductVersion;
      level?: number;
      subscriptionPrice?: string | null;
    };
    planData: Partial<AgencyBrandCountType> & {
      stripeProductId: string;
      version: StripeProductVersion;
    };
    newBrandDetails: FlatAgencyOrgRow;
    previewOverage?: number;
    agencyId: string;
    subscriptionId: string;
    sourcePlanId: string;
    currentUsage?: number;
    isAssignMode?: boolean;
    currentPlanLevel?: number;
    targetPlanLevel?: number;
    activeCoupon?: StripeSubscriptionSummaryDto["coupon"];
  };
  confirmCancelPendingDowngrade: {
    brandDetails: FlatAgencyOrgRow;
  };
  confirmCancelPendingCancellation: {
    brandDetails: FlatAgencyOrgRow;
  };
  confirmRemoveBrandFromAgencyList: {
    brandDetails: FlatAgencyOrgRow;
    allAgencySubscriptionIds: string[];
  };
  partnerBillingBrandNotInList: void;
  intelligemsWrapped: void;
  requestFeatureAccessPylon: void;
  visualBuilderRevertFeedback: {
    onConfirm?: () => void | Promise<void>;
  };
  checkoutBlockSingleProductConfirm: {
    checkoutBlockId: string;
    templateType: BlockTemplateType;
    blockType: CheckoutBlockType;
    productsToRemove: number;
    maxProductLimit: number;
  };
  approvePlanConfirmation: {
    planName: string;
    monthlyPrice: number;
    interval: string;
    intervalCount?: number | null;
    features: string[];
    subscriptionLinkId: string;
  };
}

type AiTestNameEntry = {
  status: "loading" | "ready";
  flaggedNames: FlaggedName[];
};

export type AiChat = {
  open: boolean;
  start: boolean;
  initialContext: any;
  mode:
    | "analytics"
    | "ui-actions"
    | "sitewide-analytics"
    | "experience-creation-single-agent"
    | "agentic-logic-creation"
    | undefined;
  /**
   * `agentic-logic-creation` only: the rule the open Atlas session has created
   * and is still materializing (draft, graph, and first publish in flight).
   * Set when the creation workflow's create tool runs; cleared when the
   * session ends, not when the build completes — the rule workspace keeps
   * reading it to hold its "being built" canvas until the artifacts land.
   */
  buildingRuleId: string | null;
  /**
   * `agentic-logic-creation` only: the cart run the rule workspace should be
   * showing. Atlas is the only thing that runs a cart — the canvas has no
   * picker of its own — so this is how the tool result it just received
   * reaches the graph beside it. `runToken` rises on every run so re-running
   * the same cart replays the walk animation; the evaluation itself is read
   * from the react-query cache the chat seeded, keyed on `name`.
   */
  activeScenario: AtlasActiveScenario | null;
  /**
   * Where the chat rail's ONE instance is rendered.
   *
   * `"shell"` is the layout shell's right rail beside `<main>`, which is where
   * every chat mode lives by default. `"embedded"` hands the rail to a
   * full-page dialog: the dialogs are portalled ABOVE the layout shell, so a
   * rail rendered in the shell is underneath them and cannot be reached. The
   * claiming host renders `<AiChat />` itself and the shell renders none,
   * because two mounted `AiChat`s would each open their own workflow stream
   * and both dispatch into the same `aiChat` slice.
   */
  railHost: "shell" | "embedded";
  /**
   * How the chat is framed beside the page it belongs to.
   *
   * `"rail"` is the resizable panel beside the host's content. `"page"` hands
   * the host's whole content area to the chat, laid out as the Atlas takeover
   * column: the rule builder does this while Atlas designs a rule that has no
   * graph to show yet, so the conversation is the focus until there is a chart
   * to sit beside. Only the frame changes — the one `AiChat` stays mounted, so
   * the stream running in it survives the switch. Written by the agentic-logic
   * surface (`useAtlasChatPresentation`), which resets it when it unmounts.
   */
  presentation: AiChatPresentation;
};

export type AiChatPresentation = "rail" | "page";

export type AtlasActiveScenario = {
  ruleId: string;
  name: string;
  runToken: number;
  /**
   * The request the run evaluated (surface, shipping, customer), as the
   * evaluate tool received it. Absent for a run whose input did not parse.
   */
  request?: AgenticLogicValidationScenarioInput["request"];
};

export interface IGlobalState {
  confetti: boolean;
  progresses: WebsocketToast;
  ignoreUpdateDetected: boolean;
  aiChat: AiChat;
  /**
   * The agentic-logic surface currently on screen, or null.
   *
   * Registered by whichever surface provider is mounted, and read by code that
   * lives OUTSIDE the page tree — the chat rail, which navigates to a rule it
   * just created. A pathname cannot answer this: an embedded surface is a
   * dialog taking over a host's content, and dialogs are not in the URL.
   *
   * Stored as a serializable descriptor because a surface's path builders are
   * functions; rebuild it with `agenticLogicSurfaceFromDescriptor`.
   */
  activeAgenticLogicSurface: AgenticLogicSurfaceDescriptor | null;
  aiTestNames: Record<string, AiTestNameEntry>;
  bypassUnsavedChanges: boolean;
  bottomAlert: boolean;
  dialogs: Record<keyof DialogProps, DialogProps[keyof DialogProps] | null>;
  shareSettings: ShareSettings;
}

export const initialScopedProps = {
  title: "Oops! Something went wrong!",
  message: "Please contact support@intelligems.io for assistance.",
  yesButtonText: "Contact Support",
  noButtonText: "Close",
  showButtons: true,
};

const initialGlobalState: IGlobalState = {
  confetti: false,
  progresses: {},
  bottomAlert: false,
  ignoreUpdateDetected: false,
  activeAgenticLogicSurface: null,
  aiTestNames: {},
  aiChat: {
    open: false,
    start: false,
    initialContext: undefined,
    mode: undefined,
    buildingRuleId: null,
    activeScenario: null,
    railHost: "shell",
    presentation: "rail",
  },
  bypassUnsavedChanges: false,
  dialogs: {} as Record<keyof DialogProps, DialogProps[keyof DialogProps]>,
  shareSettings: {
    showChange: true,
    showImpact: true,
    showName: true,
  },
};

const slice = createSlice({
  name: "global",
  initialState: initialGlobalState,
  reducers: {
    setIgnoreUpdateDetected: (global, value: PayloadAction<boolean>) => {
      global.ignoreUpdateDetected = value.payload;
    },
    setBypassUnsavedChanges: (global, value: PayloadAction<boolean>) => {
      global.bypassUnsavedChanges = value.payload;
    },
    setConfetti: (global, value: PayloadAction<boolean>) => {
      global.confetti = value.payload;
    },
    clearGlobal: (global) => {
      Object.assign(global, initialGlobalState);
    },
    openDialog: <K extends keyof DialogProps>(
      draft: IGlobalState,
      action: PayloadAction<{
        dialog: K;
        props: DialogProps[K];
      }>
    ) => {
      draft.dialogs[action.payload.dialog] = action.payload.props ?? null;
    },
    closeDialog: <K extends keyof DialogProps>(
      draft: IGlobalState,
      action: PayloadAction<K>
    ) => {
      delete draft.dialogs[action.payload];
    },
    setShareImpact: (draft, action: PayloadAction<boolean>) => {
      draft.shareSettings.showImpact = action.payload;
    },
    setShareChange: (draft, action: PayloadAction<boolean>) => {
      draft.shareSettings.showChange = action.payload;
    },
    setShareName: (draft, action: PayloadAction<boolean>) => {
      draft.shareSettings.showName = action.payload;
    },
    setHasBottomAlert: (draft, action: PayloadAction<boolean>) => {
      draft.bottomAlert = action.payload;
    },
    setAiTestNameLoading: (
      draft: IGlobalState,
      action: PayloadAction<string>
    ) => {
      draft.aiTestNames[action.payload] = {
        status: "loading",
        flaggedNames: [],
      };
    },
    setAiTestNameReady: (
      draft: IGlobalState,
      action: PayloadAction<{
        experienceId: string;
        flaggedNames: FlaggedName[];
      }>
    ) => {
      draft.aiTestNames[action.payload.experienceId] = {
        status: "ready",
        flaggedNames: action.payload.flaggedNames,
      };
    },
    clearAiTestName: (draft: IGlobalState, action: PayloadAction<string>) => {
      delete draft.aiTestNames[action.payload];
    },
    setActiveAgenticLogicSurface: (
      draft: IGlobalState,
      action: PayloadAction<AgenticLogicSurfaceDescriptor | null>
    ) => {
      draft.activeAgenticLogicSurface = action.payload;
    },
    setAiChat: <T extends keyof AiChat>(
      draft: IGlobalState,
      action: PayloadAction<
        { key: T; value: AiChat[T] } | { key: T; value: AiChat[T] }[]
      >
    ) => {
      if (Array.isArray(action.payload)) {
        for (const { key, value } of action.payload) {
          draft.aiChat[key] = value;
        }
      } else {
        draft.aiChat[action.payload.key] = action.payload.value;
      }
    },
  },
});

export const {
  setIgnoreUpdateDetected,
  setBypassUnsavedChanges,
  closeDialog,
  setConfetti,
  clearGlobal,
  setShareImpact,
  setShareChange,
  setShareName,
  setHasBottomAlert,
  setAiChat,
  setActiveAgenticLogicSurface,
  setAiTestNameLoading,
  setAiTestNameReady,
  clearAiTestName,
} = slice.actions;

type VoidKeys<T> = {
  [K in keyof T]: void extends T[K] ? K : never;
}[keyof T];

export type VoidDialogProps = Pick<
  DialogProps,
  NonNullable<VoidKeys<DialogProps>>
>;

// Let's make sure showDialog is typed correctly
export function openDialog<K extends keyof VoidDialogProps>(
  dialog: K
): PayloadAction<{ dialog: K; props: DialogProps[K] }>;

export function openDialog<K extends keyof DialogProps>(payload: {
  dialog: K;
  props: DialogProps[K];
}): PayloadAction<{ dialog: K; props: DialogProps[K] }>;

export function openDialog<K extends keyof DialogProps>(
  payload:
    | {
        dialog: K;
        props: DialogProps[K];
      }
    | K
) {
  if (typeof payload === "string") {
    return slice.actions.openDialog({
      dialog: payload,
      props: undefined,
    }) as PayloadAction<{ dialog: K; props: DialogProps[K] }>;
  } else {
    return slice.actions.openDialog(payload) as PayloadAction<{
      dialog: K;
      props: DialogProps[K];
    }>;
  }
}

export const sendWebsocketMessage = createAction<WebsocketData>(
  "global/sendWebsocketMessage"
);

export const isDialogOpen = (dialog: keyof DialogProps) => (state: RootState) =>
  dialog in state.global.dialogs;

export const getIgnoreUpdateDetected = (state: RootState) => {
  return state.global.ignoreUpdateDetected;
};

export const getBypassUnsavedChanges = (state: RootState) => {
  return state.global.bypassUnsavedChanges;
};

export const selectAiTestNameState =
  (experienceId: string) => (state: RootState) =>
    state.global.aiTestNames[experienceId];

export const getProgresses = () => (state: RootState) => {
  return state.global.progresses;
};

export const getShareSettings = () => (state: RootState) =>
  state.global.shareSettings;

type AiChatProps<T = AiNodeContext> = Omit<
  RootState["global"]["aiChat"],
  "initialContext"
> & {
  initialContext: T | undefined;
};

// Boundary cast: the redux slice stores `initialContext` as `any` because
// different chat modes write different shapes. Each consumer specifies the
// shape it expects via the generic; one localised `as` here replaces casts
// at every call site.
export const getAiChatProps = <T = AiNodeContext>(state: RootState) =>
  state.global.aiChat as AiChatProps<T>;

export const getIsAiChatOpen = (state: RootState) => {
  return state.global.aiChat.open;
};

/**
 * Whether the layout shell owns the chat rail. False while a full-page dialog
 * has claimed it (see {@link AiChat.railHost}); the shell renders nothing then,
 * so the rail exists exactly once on screen.
 */
/**
 * The agentic-logic surface on screen, in its stored serializable form. Rebuild
 * it with `agenticLogicSurfaceFromDescriptor` before using its path builders.
 */
export const getActiveAgenticLogicSurface = (
  state: RootState
): AgenticLogicSurfaceDescriptor | null =>
  state.global.activeAgenticLogicSurface;

export const getIsShellRailHost = (state: RootState): boolean =>
  state.global.aiChat.railHost === "shell";

/**
 * How the open chat is framed; see {@link AiChat.presentation}. Mode-guarded
 * like {@link getAtlasBuildingRuleId}: only the rule builder ever takes the
 * page, so a value left behind by a mode switch never frames another chat.
 */
export const getAiChatPresentation = (state: RootState): AiChatPresentation =>
  state.global.aiChat.open &&
  state.global.aiChat.mode === "agentic-logic-creation"
    ? state.global.aiChat.presentation
    : "rail";

/**
 * The rule the open Atlas creation session is still building, or null. The
 * mode guard is baked in so a value left behind by a mode switch can never
 * leak into another chat mode's reading of the panel.
 */
export const getAtlasBuildingRuleId = (state: RootState): string | null =>
  state.global.aiChat.mode === "agentic-logic-creation"
    ? state.global.aiChat.buildingRuleId
    : null;

/**
 * The cart run Atlas last produced, or null. Mode-guarded for the same reason
 * as {@link getAtlasBuildingRuleId}: a run left behind by a mode switch must
 * never dim another chat mode's canvas.
 */
export const getAtlasActiveScenario = (
  state: RootState
): AtlasActiveScenario | null =>
  state.global.aiChat.mode === "agentic-logic-creation"
    ? state.global.aiChat.activeScenario
    : null;

/**
 * Whether an Atlas rule-creation session is running. The mode alone does not
 * mean this — the open panel keeps `mode` set on the rule workspace; only a
 * stored entry context marks an active build (see `CreateRuleTab.openChat`).
 */
export const getIsAtlasCreationSessionActive = (state: RootState): boolean =>
  state.global.aiChat.mode === "agentic-logic-creation" &&
  state.global.aiChat.initialContext != null;

export const getIsDrawerOpen = (state: RootState) => {
  return !state.appSettings.sideNavCollapsed;
};

export const globalReducer = slice.reducer;

export type OpenDialogAction<K extends keyof DialogProps = keyof DialogProps> =
  PayloadAction<{
    dialog: K;
    props: DialogProps[K];
  }>;
